import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';

/**
 * Collapsible "intro" card at the top of each zone tab.
 *
 * Looks like a soft, embossed panel: warm gradient background, decorative
 * blur blob in the corner, generous padding. Title is always visible; the
 * description animates in/out as the user toggles.
 *
 * Parent owns the `collapsed` boolean.
 */
export function CollapsibleInfoBox({ icon: Icon, title, description, collapsed, onToggle }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            role="button"
            tabIndex={0}
            onClick={onToggle}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
            }}
            aria-expanded={!collapsed}
            className={clsx(
                'relative overflow-hidden rounded-2xl cursor-pointer select-none',
                'bg-gradient-to-br from-brand-50 via-surface to-secondary-50/40',
                'ring-1 ring-brand-100/80',
                'shadow-soft hover:shadow-card',
                'transition-shadow duration-300 ease-smooth',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
            )}
        >
            <span aria-hidden="true" className="absolute -top-12 -end-10 w-60 h-60 rounded-pill blur-3xl bg-brand-200/40 pointer-events-none" />
            <span aria-hidden="true" className="absolute -bottom-16 -start-8 w-40 h-40 rounded-pill blur-3xl bg-secondary-200/30 pointer-events-none" />

            <div className="relative p-5 sm:p-6">
                <div className="flex items-center gap-4">
                    <span className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-ink-inverse shadow-md ring-2 ring-white">
                        <Icon className="w-5 h-5" strokeWidth={2} />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3">
                            <h3 className="text-base sm:text-lg font-bold text-ink leading-tight truncate">{title}</h3>
                            <ChevronDown
                                className={clsx(
                                    'w-5 h-5 shrink-0 text-ink-subtle transition-transform duration-300',
                                    !collapsed && 'rotate-180',
                                )}
                                strokeWidth={2.5}
                            />
                        </div>
                        <AnimatePresence initial={false}>
                            {!collapsed && description && (
                                <motion.p
                                    key="desc"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
                                    className="text-ink-muted leading-relaxed text-sm mt-2 overflow-hidden"
                                >
                                    {description}
                                </motion.p>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

export default CollapsibleInfoBox;
